import axios from "axios";

export const METHOD_GET = "GET",
  METHOD_POST = "POST",
  METHOD_PUT = "PUT",
  METHOD_DELETE = "DELETE",
  METHOD_PATCH = "PATCH";

/**
 * Join the API base with a relative path.
 *
 * These were previously concatenated directly, so REACT_APP_BASE_URL had to
 * end in "/" or every request in the app silently became a malformed URL
 * ("http://host:3000" + "user/login" -> "http://host:3000user/login"). A
 * missing character in an env file should not be able to take down the app.
 */
export function joinUrl(base, path) {
  const cleanBase = String(base || "").replace(/\/+$/, "");
  const cleanPath = String(path || "").replace(/^\/+/, "");
  if (!cleanBase) {
    // Fall back to a same-origin relative path rather than requesting
    // "undefinedputh/to/thing".
    return `/${cleanPath}`;
  }
  return `${cleanBase}/${cleanPath}`;
}

function send(method, url, data, header) {
  switch (method) {
    case METHOD_GET:
      return axios.get(url, header);
    case METHOD_POST:
      return axios.post(url, data, header);
    case METHOD_PUT:
      return axios.put(url, data, header);
    case METHOD_PATCH:
      return axios.patch(url, data, header);
    case METHOD_DELETE:
      return axios.delete(url, header);
    default:
      return Promise.reject(new Error("Method not supported"));
  }
}

// A refused token (401, or the bare 403 the API sends for an expired or
// invalid one) or a suspended account; not a 403 the API explains in JSON
// ("slot not available", "Pro required"), which used to trigger a token
// refresh and send the same request a second time.
function sessionProblem(res) {
  if (!res) return false;
  if (res.status === 401) return true;
  if (res.status !== 403) return false;
  const body = res.data;
  if (!body || typeof body !== "object") return true;
  return /suspended/i.test(body.message || "");
}

export default async function axiosFactory({
  url,
  method,
  data,
  isFake = false,
}) {
  // An absolute URL is used as-is; only relative paths get the API base.
  if (/^https?:\/\//i.test(url)) {
    isFake = true;
  }
  const urlToUse = isFake ? url : joinUrl(process.env.REACT_APP_BASE_URL, url);
  let response = { status: "", data: "", error: "" };
  try {
    try {
      response = await send(method, urlToUse, data, getHeader(method, data, url, isFake));
    } catch (error) {
      // An expired access token: refresh it and replay the request once.
      // Only for our own API — a 401 from a third party (an image host, a
      // payment provider) says nothing about this session, and treating it
      // as one used to log people out.
      const status = error.response && error.response.status;
      if (isFake || !sessionProblem(error.response)) throw error;
      if ((await refreshAccessToken()) !== "refreshed") throw error;
      response = await send(method, urlToUse, data, getHeader(method, data, url, isFake));
    }
  } catch (error) {
    if (!error.response) {
      response.status = 0;
      response.error = "Network error";
      response.data =
        error.message || "Unable to reach the server. Please try again.";
      return {
        data: response.data,
        status: response.status,
        error: response.error,
      };
    }

    const body = error.response.data || {};
    // The API's machine-readable reason ("PAYMENT_FAILED"...), so callers can
    // show their own translated message instead of the server's French text.
    if (body && typeof body.code === "string") response.code = body.code;
    // `status` below folds 429 and 502-504 into 400 for older callers; the
    // real code and Retry-After let a page tell "rate limited" or "server
    // down" from "not found" (helpers/loadResult.js).
    response.httpStatus = error.response.status;
    const retryAfter = (error.response.headers || {})["retry-after"];
    if (retryAfter) response.retryAfter = retryAfter;
    switch (error.response.status) {
      case 401:
        response.status = 401;
        response.error = "Unauthorized";
        response.data = body.message;
        break;
      case 403:
        response.status = 403;
        response.error = "Forbidden";
        response.data = body.message;
        break;
      case 404:
        response.status = 404;
        response.error = "Not found";
        response.data = body.message;
        break;
      case 500:
        response.status = 500;
        response.error = "Internal server error";
        response.data = body.message;
        break;
      default:
        response.status = 400;
        response.error = "Something went wrong";
        response.data = body.message;
        break;
    }
  }
  return {
    data: response.data,
    status: response.status,
    error: response.error,
    code: response.code,
    httpStatus: response.httpStatus,
    retryAfter: response.retryAfter,
  };
}

function getHeader(method, data, url, isFake) {
  let header = {
    "Content-Type": "application/json",
  };
  // The access token goes to our API only, never to a third-party URL.
  if (!isFake) {
    header.authorization = `Bearer ${localStorage.getItem("jwtAccessToken")}`;
  }
  if (method === METHOD_DELETE) {
    return { headers: header, data: data };
  }
  return { headers: header };
}

export function logout({ redirect = true } = {}) {
  localStorage.removeItem("user");
  localStorage.removeItem("jwtAccessToken");
  localStorage.removeItem("jwtRefreshToken");
  if (redirect) window.location.href = "/";
}

// Several requests can fail at once when the token expires; they share one
// refresh instead of racing each other.
let refreshing = null;

/**
 * Resolves to "refreshed", "none" (no refresh token: a visitor) or "failed".
 * Logs out only when the server rejects the refresh token itself. A network
 * blip during the refresh leaves the session in place.
 */
export function refreshAccessToken({ redirect = true } = {}) {
  const refreshToken = localStorage.getItem("jwtRefreshToken");
  if (!refreshToken) return Promise.resolve("none");
  if (!refreshing) {
    const url = joinUrl(process.env.REACT_APP_BASE_URL, "user/refresh");
    refreshing = axios
      .get(url, {
        headers: { authorization: `Bearer ${refreshToken}` },
      })
      .then((response) => {
        const token = response.data?.data?.accessToken;
        if (!token) return "failed";
        localStorage.setItem("jwtAccessToken", token);
        return "refreshed";
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        if (status === 401 || status === 403) logout({ redirect });
        return "failed";
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}
